import { forwardRef } from 'react';
import { useCoverLetterEditor } from '../../context/CoverLetterContext';
import type { CoverLetterContent } from '../../models/cover-letter';

const CoverLetterCanvas = forwardRef<HTMLDivElement>((_, ref) => {
    const { currentVersion, updateContent } = useCoverLetterEditor();

    if (!currentVersion || !currentVersion.content) return null;

    const { content } = currentVersion;

    const handleUpdate = (path: string, value: any) => {
        // Deep clone content
        const newContent: CoverLetterContent = JSON.parse(JSON.stringify(content));

        // Simple path traverser
        const parts = path.split('.');
        let current: any = newContent;
        for (let i = 0; i < parts.length - 1; i++) {
            current = current[parts[i]];
        }

        // Handle array updates if path ends with index like "middle_paragraphs.0"
        const lastPart = parts[parts.length - 1];
        if (!isNaN(Number(lastPart)) && Array.isArray(current)) {
            current[Number(lastPart)] = value;
        } else {
            current[lastPart] = value;
        }

        updateContent(newContent);
    };

    const getFromName = () => {
        return content.header_info.from_contact.split('\n')[0] || 'Your Name';
    };

    return (
        <div
            ref={ref}
            className="bg-white text-black font-serif min-h-[1100px] shadow-2xl p-[50px] relative transition-all print:shadow-none print:p-[15mm] print:w-full print:h-auto print:min-h-0 print:overflow-visible mx-auto w-full max-w-[850px] cover-letter-print-container"
        >
            <style type="text/css" media="print">
                {`
                    @page { size: auto;  margin: 0mm; }
                    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                    .cover-letter-print-container {
                        -webkit-box-decoration-break: clone;
                        box-decoration-break: clone;
                    }
                `}
            </style>

            <div className="flex flex-col gap-6 text-base leading-relaxed text-gray-800">
                {/* Header Info */}
                <div className="flex justify-between mb-2">
                    {/* From Contact */}
                    <div
                        contentEditable
                        suppressContentEditableWarning
                        className="whitespace-pre-wrap outline-none focus:bg-blue-50 p-1 rounded min-w-[200px]"
                        onBlur={(e) => handleUpdate('header_info.from_contact', e.currentTarget.innerText)}
                    >
                        {content.header_info.from_contact}
                    </div>
                </div>

                <div className="flex flex-col gap-2">
                    {/* Date */}
                    <div
                        contentEditable
                        suppressContentEditableWarning
                        className="outline-none focus:bg-blue-50 p-1 rounded"
                        onBlur={(e) => handleUpdate('header_info.date', e.currentTarget.innerText)}
                    >
                        {content.header_info.date}
                    </div>

                    {/* To Contact */}
                    <div
                        contentEditable
                        suppressContentEditableWarning
                        className="whitespace-pre-wrap outline-none focus:bg-blue-50 p-1 rounded"
                        onBlur={(e) => handleUpdate('header_info.to_contact', e.currentTarget.innerText)}
                    >
                        {content.header_info.to_contact}
                    </div>
                </div>

                {/* Salutation */}
                <div
                    contentEditable
                    suppressContentEditableWarning
                    className="outline-none focus:bg-blue-50 p-1 rounded"
                    onBlur={(e) => handleUpdate('letter_body.salutation', e.currentTarget.innerText)}
                >
                    {content.letter_body.salutation}
                </div>

                {/* Opening Paragraph */}
                <div
                    contentEditable
                    suppressContentEditableWarning
                    className="outline-none focus:bg-blue-50 p-1 rounded indent-0 whitespace-pre-wrap"
                    onBlur={(e) => handleUpdate('letter_body.opening_paragraph', e.currentTarget.innerText)}
                >
                    {content.letter_body.opening_paragraph}
                </div>

                {/* Middle Paragraphs */}
                <div className="flex flex-col gap-4">
                    {content.letter_body.middle_paragraphs.map((para, idx) => (
                        <div
                            key={idx}
                            contentEditable
                            suppressContentEditableWarning
                            className="outline-none focus:bg-blue-50 p-1 rounded whitespace-pre-wrap"
                            onBlur={(e) => handleUpdate(`letter_body.middle_paragraphs.${idx}`, e.currentTarget.innerText)}
                        >
                            {para}
                        </div>
                    ))}
                </div>

                {/* Closing Paragraph */}
                <div
                    contentEditable
                    suppressContentEditableWarning
                    className="outline-none focus:bg-blue-50 p-1 rounded whitespace-pre-wrap"
                    onBlur={(e) => handleUpdate('letter_body.closing_paragraph', e.currentTarget.innerText)}
                >
                    {content.letter_body.closing_paragraph}
                </div>

                {/* Sign Off */}
                <div className="mt-8">
                    <div
                        contentEditable
                        suppressContentEditableWarning
                        className="outline-none focus:bg-blue-50 p-1 rounded"
                        onBlur={(e) => handleUpdate('letter_body.sign_off', e.currentTarget.innerText)}
                    >
                        {content.letter_body.sign_off}
                    </div>
                    <div className="mt-4 font-bold">
                        {getFromName()}
                    </div>
                </div>
            </div>
        </div>
    );
});

export default CoverLetterCanvas;
